/**
 * Ícone de Status do WhatsApp
 * 
 * Representa o ícone circular com traços usado pelo WhatsApp Web
 * para indicar a seção de Status/Stories
 * 
 * @version 1.0.0
 * @release 2025-02-08
 */

import * as React from 'react';
import { cn } from '@/lib/utils';

interface WhatsAppStatusIconProps extends React.SVGAttributes<SVGSVGElement> {
  /** Se há status ativo (círculo preenchido) */
  hasActiveStatus?: boolean;
  /** Tamanho do ícone */
  size?: number;
}

/**
 * Ícone SVG customizado que representa o Status do WhatsApp
 * Similar ao usado no WhatsApp Web (círculo com traço/dashes)
 */
export function WhatsAppStatusIcon({
  hasActiveStatus = false,
  size = 20,
  className,
  ...props
}: WhatsAppStatusIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('', className)}
      {...props}
    >
      {hasActiveStatus ? (
        // Círculo com segmentos preenchidos (tem status ativo)
        <>
          {/* Círculo base com dash array para criar efeito segmentado */}
          <circle
            cx="12"
            cy="12"
            r="10"
            strokeDasharray="15.7 5"
            strokeDashoffset="0"
            className="text-primary"
            stroke="currentColor"
          />
          {/* Pequeno círculo interno indicando conteúdo */}
          <circle
            cx="12"
            cy="12"
            r="3"
            fill="currentColor"
            stroke="none"
            className="text-primary"
          />
        </>
      ) : (
        // Círculo tracejado simples (sem status)
        <>
          <circle
            cx="12"
            cy="12"
            r="10"
            strokeDasharray="4 4"
            className="text-muted-foreground"
          />
          {/* Ícone de + no centro */}
          <line x1="12" y1="8" x2="12" y2="16" className="text-muted-foreground" />
          <line x1="8" y1="12" x2="16" y2="12" className="text-muted-foreground" />
        </>
      )}
    </svg>
  );
}

/**
 * Variante simplificada do ícone (apenas círculo tracejado)
 * Usada em contextos menores
 */
export function WhatsAppStatusIconSimple({
  size = 16,
  className,
  ...props
}: Omit<WhatsAppStatusIconProps, 'hasActiveStatus'>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('', className)}
      {...props}
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        strokeDasharray="14 4.5"
        strokeDashoffset="0"
      />
    </svg>
  );
}
